import {
  type GetApiariesError,
  useGetApiaries,
} from '@/generated/http-clients/v3ApiComponents'
import type { DeploymentLabel } from '@/generated/http-clients/v3ApiSchemas'
import type { UseQueryResult } from '@tanstack/react-query'
import type React from 'react'
import { createContext, useContext, useMemo } from 'react'

export type DeploymentLabelOption = {
  label: DeploymentLabel
  value: DeploymentLabel
}

interface ApiariesContextType {
  apiaries: UseQueryResult<DeploymentLabel[], GetApiariesError>
  deploymentLabels: DeploymentLabelOption[]
  isLoading: boolean
  error: GetApiariesError | null
}

const ApiariesContext = createContext<ApiariesContextType | undefined>(
  undefined,
)

export const ApiariesProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const apiaries = useGetApiaries(
    {},
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      // Cache data between route changes to avoid unnecessary refetches
      gcTime: 10 * 60 * 1000, // 10 minutes cache time
      // Disable automatic refetching to prevent unnecessary API calls
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
  )

  const deploymentLabels = useMemo<DeploymentLabelOption[]>(() => {
    if (!apiaries.data || apiaries.data.length === 0) {
      return []
    }
    return apiaries.data.map((label) => ({ label, value: label }))
  }, [apiaries.data])

  const value = useMemo(
    () => ({
      apiaries,
      deploymentLabels,
      isLoading: apiaries.isLoading,
      error: apiaries.error,
    }),
    [apiaries, deploymentLabels],
  )

  return (
    <ApiariesContext.Provider value={value}>
      {children}
    </ApiariesContext.Provider>
  )
}

export const useApiaries = () => {
  const context = useContext(ApiariesContext)
  if (!context) {
    throw new Error('useApiaries must be used within an ApiariesProvider')
  }
  return context
}
